import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, Smartphone, Mail, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

// mfaData: { mfa_token, mfa_enrolled, mfa_method, methods, email, name }
export function MfaFlow({ mfaData, onSuccess, onCancel }) {
  const { mfa_token, mfa_enrolled, mfa_method, email } = mfaData;
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");
  // enrollment
  const [method, setMethod] = useState(null); // chosen during enrollment
  const [totp, setTotp] = useState(null); // { secret, qr }
  const [emailSentTo, setEmailSentTo] = useState(null);

  const chooseMethod = async (m) => {
    setBusy(true); setCode("");
    try {
      const { data } = await api.post("/auth/mfa/setup", { mfa_token, method: m });
      setMethod(m);
      if (m === "totp") setTotp({ secret: data.secret, qr: data.qr });
      else setEmailSentTo(data.sent_to);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const resendEmail = async () => {
    setBusy(true);
    try { await api.post("/auth/mfa/resend", { mfa_token }); toast.success("A new code is on its way"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitEnrollCode = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/verify-setup", { mfa_token, method, code });
      toast.success("Two-factor authentication enabled");
      onSuccess(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitLoginCode = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/verify", { mfa_token, code });
      onSuccess(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // ---- ENROLLED: just ask for a code ----
  if (mfa_enrolled) {
    return (
      <div className="space-y-5" data-testid="mfa-challenge">
        <Header title="Two-factor verification"
          subtitle={mfa_method === "email"
            ? <>We sent a 6-digit code to <span className="font-medium text-foreground">{email}</span>.</>
            : "Enter the 6-digit code from your authenticator app."} />
        <CodeForm code={code} setCode={setCode} busy={busy} onSubmit={submitLoginCode} label="Verification code" />
        {mfa_method === "email" && (
          <button type="button" onClick={resendEmail} disabled={busy}
            className="text-xs text-muted-foreground hover:text-foreground" data-testid="mfa-resend-btn">
            Didn't get it? Resend code
          </button>
        )}
        <BackBtn onCancel={onCancel} />
      </div>
    );
  }

  // ---- NOT ENROLLED: choose method ----
  if (!method) {
    return (
      <div className="space-y-5" data-testid="mfa-enroll-choose">
        <Header title="Set up two-factor authentication"
          subtitle="Your role requires an extra layer of security. Choose how you'd like to verify sign-ins." />
        <div className="space-y-3">
          <MethodCard testId="mfa-choose-totp" onClick={() => chooseMethod("totp")} disabled={busy}
            Icon={Smartphone} title="Authenticator app"
            desc="Use Google Authenticator, Authy or 1Password (recommended)." />
          <MethodCard testId="mfa-choose-email" onClick={() => chooseMethod("email")} disabled={busy}
            Icon={Mail} title="Email code"
            desc="Get a one-time code by email each time you sign in." />
        </div>
        <BackBtn onCancel={onCancel} />
      </div>
    );
  }

  // ---- NOT ENROLLED: TOTP setup ----
  if (method === "totp") {
    return (
      <div className="space-y-5" data-testid="mfa-enroll-totp">
        <Header title="Scan the QR code"
          subtitle="Scan this with your authenticator app, then enter the 6-digit code it shows." />
        {totp?.qr && <img src={totp.qr} alt="Authenticator QR code" className="mx-auto h-44 w-44 border border-border rounded-md" data-testid="mfa-totp-qr" />}
        {totp?.secret && (
          <p className="text-center text-xs text-muted-foreground">
            Can't scan? Enter this key manually:<br />
            <span className="font-mono text-foreground break-all" data-testid="mfa-totp-secret">{totp.secret}</span>
          </p>
        )}
        <CodeForm code={code} setCode={setCode} busy={busy} onSubmit={submitEnrollCode} label="Code from app" />
        <BackBtn onCancel={() => { setMethod(null); setTotp(null); setCode(""); }} label="Choose another method" />
      </div>
    );
  }

  // ---- NOT ENROLLED: Email setup ----
  return (
    <div className="space-y-5" data-testid="mfa-enroll-email">
      <Header title="Verify your email"
        subtitle={<>We sent a 6-digit code to <span className="font-medium text-foreground">{emailSentTo || email}</span>.</>} />
      <CodeForm code={code} setCode={setCode} busy={busy} onSubmit={submitEnrollCode} label="Verification code" />
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => chooseMethod("email")} disabled={busy}
          className="text-xs text-muted-foreground hover:text-foreground" data-testid="mfa-enroll-email-resend">Resend code</button>
        <span className="text-muted-foreground">·</span>
        <button type="button" onClick={() => { setMethod(null); setEmailSentTo(null); setCode(""); }}
          className="text-xs text-muted-foreground hover:text-foreground">Choose another method</button>
      </div>
    </div>
  );
}

const CodeForm = ({ code, setCode, busy, onSubmit, label }) => (
  <form onSubmit={onSubmit} className="space-y-4">
    <div className="space-y-2">
      <Label htmlFor="mfa-code">{label}</Label>
      <Input id="mfa-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
        required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
        placeholder="000000" className="rounded-sm text-center tracking-[0.5em] text-lg font-mono"
        data-testid="mfa-code-input" autoFocus />
    </div>
    <Button type="submit" disabled={busy || code.length < 6} className="w-full rounded-sm" data-testid="mfa-verify-btn">
      {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Verify & continue
    </Button>
  </form>
);

const Header = ({ title, subtitle }) => (
  <div className="text-center space-y-2">
    <div className="mx-auto h-11 w-11 rounded-full bg-primary/10 grid place-items-center">
      <ShieldCheck className="h-5 w-5 text-primary" />
    </div>
    <h2 className="font-heading text-xl font-semibold tracking-tight">{title}</h2>
    <p className="text-sm text-muted-foreground">{subtitle}</p>
  </div>
);

const MethodCard = ({ Icon, title, desc, onClick, disabled, testId }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testId}
    className="w-full flex items-start gap-3 text-left border border-border rounded-md p-4 hover:bg-accent hover:border-primary/40 transition-colors disabled:opacity-60">
    <Icon className="h-5 w-5 text-primary mt-0.5 shrink-0" />
    <div>
      <p className="text-sm font-medium">{title}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
    </div>
  </button>
);

const BackBtn = ({ onCancel, label = "Back to sign in" }) => (
  <button type="button" onClick={onCancel} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mx-auto" data-testid="mfa-back-btn">
    <ArrowLeft className="h-3.5 w-3.5" />{label}
  </button>
);
